import React, { lazy, Suspense, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

import { usePageDetails } from "../../hooks/usePageDetails";
import CreateHeading from "../../components/CreateHeading/CreateHeading";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import FormInput from "../../components/FormInput/FormInput";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import FormActionButtons from "../../components/FormActionButtons/FormActionButtons";
import {
  useCreateFileMutation,
  useEditFileMutation,
  useLazyGetSingleFileDetailsQuery,
} from "../../app/api/fileApi";
import FormTextArea from "../../components/FormTextArea/FormTextArea";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useAppSelector } from "../../redux/store";
import { base64DecodeUtf8, base64EncodeUtf8 } from "../../utils/base64Encoder";

export interface InitialFormValues {
  file_name: string;
  config_template: string;
  file_name_template: string;
  is_active: boolean;
  brand_id: string | null;
  family_id: string | null;
  model_id: string | null;
  firmware_id: string | null;
  template_id: string | null;
}

const CreateFile: React.FC = () => {
  const { id, pageType } = usePageDetails();
  const LazyFileFormSelects = lazy(() => import("./FileFormSelects"));
  const MemoizedFileFormSelects = React.memo(LazyFileFormSelects);
  const [decodedConfigTemplate, setDecodedConfigTemplate] =
    useState<string>("");

  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );

  const [createFileMutation] = useCreateFileMutation();
  const [getSingleFileDetailsQuery, getSingleFileDetailsResponse] =
    useLazyGetSingleFileDetailsQuery();
  const [editFileMutation] = useEditFileMutation();

  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    file_name: Yup.string()
      .required("File name is required")
      .min(2, "File name must be at least 2 character")
      .max(255, "File name must be at most 255 characters"),
    file_name_template: Yup.string()
      .required("File name template is required")
      .min(2, "File name template must be at least 2 character")
      .max(255, "File name template must be at most 255 characters"),
    config_template: Yup.string()
      .required("Config Template is required")
      .min(2, "Config Template must be at least 2 character"),
    is_active: Yup.boolean(),
    brand_id: Yup.string().required("Brand is required"),
    family_id: Yup.string().required("Family is required"),
    model_id: Yup.string().required("Model is required"),
    template_id: Yup.string().required("Template is required"),
    firmware_id:
      pageType === "create"
        ? Yup.string().required("Firmware is required")
        : Yup.string(),
  });

  const firmwareIdFromGetSingleApiResponse = (
    getSingleFileDetailsResponse?.data?.data?.firmware_id ?? ""
  ).replace(/-/g, "");

  const initialValues: InitialFormValues = {
    file_name: getSingleFileDetailsResponse?.data?.data?.file_name ?? "",
    config_template: decodedConfigTemplate ?? "",
    file_name_template:
      getSingleFileDetailsResponse?.data?.data?.file_name_template ?? "",
    firmware_id:
      pageType !== "create"
        ? (firmwareIdFromGetSingleApiResponse ?? "")
        : (persistDropdownSelector["firmware_selector"]?.value ?? ""),
    is_active: getSingleFileDetailsResponse?.data?.data?.is_active ?? true,
    brand_id: persistDropdownSelector["brand_selector"]?.value ?? "",
    family_id: persistDropdownSelector["family_selector"]?.value ?? "",
    model_id: persistDropdownSelector["model_selector"]?.value ?? "",
    template_id: persistDropdownSelector["template_selector"]?.value ?? "",
  };

  const decodeConfigTemplateToUtf8 = async (configTemplate: string) => {
    const encodedConfigTemplate = await base64DecodeUtf8(configTemplate);
    return encodedConfigTemplate;
  };

  const getSingleTemplateDetails = async () => {
    try {
      const response = await getSingleFileDetailsQuery({
        brand_id: persistDropdownSelector["brand_selector"]?.value,
        family_id: persistDropdownSelector["family_selector"]?.value,
        model_id: persistDropdownSelector["model_selector"]?.value,
        template_id: persistDropdownSelector["template_selector"]?.value,
        file_id: id,
      }).unwrap();

      try {
        const decodedConfigTemplate = await decodeConfigTemplateToUtf8(
          response?.data?.config_template
        );

        setDecodedConfigTemplate(decodedConfigTemplate);
      } catch (error) {
        setDecodedConfigTemplate(response?.data?.config_template);
      }
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Get File Details Error",
      });
    }
  };

  useEffect(() => {
    if (pageType !== "create") {
      getSingleTemplateDetails();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleSubmit = async (
    values: InitialFormValues,
    { setFieldError }: FormikHelpers<InitialFormValues>
  ) => {
    const {
      file_name,
      config_template,
      file_name_template,
      firmware_id,
      brand_id,
      family_id,
      model_id,
      template_id,
      is_active,
    } = values;

    const encodedConfigTemplate = await base64EncodeUtf8(config_template);

    const body = {
      file_name,
      config_template: encodedConfigTemplate,
      file_name_template,
      firmware_id,
      ...(pageType === "edit" ? { is_active } : {}),
    };

    const successMessage =
      pageType === "create"
        ? "File created successfully."
        : "File updated successfully.";

    try {
      const response =
        pageType === "create"
          ? await createFileMutation({
              body,
              brand_id,
              family_id,
              model_id,
              template_id,
            }).unwrap()
          : await editFileMutation({
              body,
              brand_id,
              family_id,
              model_id,
              template_id,
              file_id: id,
            }).unwrap();
      if (response?.status_code === 200 || response?.status_code === 201) {
        handleApiResponse({
          type: "success",
          message: successMessage,
          toastId: "Create File",
        });
        navigate("/provisioning/file");
      }
    } catch (error: any) {
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Create File Error",
        setFieldError,
      });
      console.log(error);
    }
  };

  return (
    <Layout>
      <CreateHeading label={"File"} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          dirty,
        }) => {
          const disable =
            pageType === "create"
              ? !values.file_name ||
                !values.config_template ||
                !values.file_name_template ||
                !values.brand_id ||
                !values.family_id ||
                !values.model_id ||
                !values.firmware_id ||
                !values.template_id
              : !dirty;

          return (
            <>
              <Form className="w-100 mb-5 pb-5">
                <Col sm={6}>
                  {pageType === "view" && (
                    <Row className="my-2">
                      <FormInput
                        label="Template ID"
                        type="text"
                        name="id"
                        placeholder="Enter template name"
                        labelClassName="m-0 fw-500"
                        controlClassName="bg-white fs-15-point-5 h-48"
                        value={
                          getSingleFileDetailsResponse?.data?.data?.id ?? ""
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required={false}
                        isValid={touched.file_name && !errors.file_name}
                        isInvalid={touched.file_name && !!errors.file_name}
                        touched={touched.file_name}
                        error={errors.file_name}
                        disabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  {pageType === "create" ? (
                    <Suspense fallback={<div></div>}>
                      <MemoizedFileFormSelects
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldTouched={setFieldTouched}
                      />
                    </Suspense>
                  ) : (
                    <>
                      {" "}
                      <Row className="my-2">
                        <FormInput
                          label="Brand Name"
                          type="text"
                          name="brand_name"
                          placeholder="Brand name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleFileDetailsResponse?.data?.data
                              ?.brand_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Family Name"
                          type="text"
                          name="family_name"
                          placeholder="Family name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleFileDetailsResponse?.data?.data
                              ?.family_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Model Name"
                          type="text"
                          name="model_name"
                          placeholder="Model name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleFileDetailsResponse?.data?.data
                              ?.model_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Firmware Version"
                          type="text"
                          name="firmware_version"
                          placeholder="Firmware Version"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleFileDetailsResponse?.data?.data
                              ?.firmware_version ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Template Name"
                          type="text"
                          name="template_name"
                          placeholder="Template Name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleFileDetailsResponse?.data?.data
                              ?.template_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                    </>
                  )}

                  <Row className="my-2">
                    <FormInput
                      label="File Name"
                      type="text"
                      name="file_name"
                      placeholder="Enter file name"
                      labelClassName="m-0 fw-500"
                      controlClassName="bg-white fs-15-point-5 h-48"
                      value={values.file_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required={true}
                      isValid={touched.file_name && !errors.file_name}
                      isInvalid={touched.file_name && !!errors.file_name}
                      touched={touched.file_name}
                      error={errors.file_name}
                      disabled={pageType === "view"}
                    />
                  </Row>
                  <Row className="my-2">
                    <FormInput
                      label="File Name Template"
                      type="text"
                      name="file_name_template"
                      placeholder="Enter file name template"
                      labelClassName="m-0 fw-500"
                      controlClassName="bg-white fs-15-point-5 h-48"
                      value={values.file_name_template}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required={true}
                      isValid={
                        touched.file_name_template && !errors.file_name_template
                      }
                      isInvalid={
                        touched.file_name_template &&
                        !!errors.file_name_template
                      }
                      touched={touched.file_name_template}
                      error={errors.file_name_template}
                      disabled={pageType === "view"}
                    />
                  </Row>
                  <Row className="my-2">
                    <Col>
                      <FormTextArea
                        value={values.config_template}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.config_template}
                        error={errors.config_template}
                        name="config_template"
                        label="Config Template"
                        placeholder="Enter config template"
                        labelClassName="m-0 fw-500"
                        required={true}
                        disabled={pageType === "view"}
                      />{" "}
                    </Col>
                  </Row>
                  {pageType !== "create" && (
                    <Row className="ms-0 mt-4">
                      <ToggleSwitch
                        activeStatusText="Active"
                        inActiveStatusText="Inactive"
                        value={values.is_active}
                        onChange={(val: boolean) => {
                          setFieldValue("is_active", val);
                        }}
                        key={String(values.is_active)}
                        isDisabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  <FormActionButtons
                    disable={disable}
                    isSubmitting={isSubmitting}
                    navigateUrl="/provisioning/file"
                  />
                </Col>
              </Form>
            </>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default CreateFile;
